/**
 * Dutch genealogical name normalization utilities.
 *
 * Handles: given name variants (Jan/Johannes), surname normalization,
 * tussenvoegsel normalization, OCR error correction, patronymic detection.
 */

import { TUSSENVOEGSELS } from '../../shared/voorouderGroepen.mjs';
import variantenData from '../../shared/givenNameVariants.json';

// ---------------------------------------------------------------------------
// Voornamen — één canonieke vorm (NC #819)
// ---------------------------------------------------------------------------

/**
 * NC #819 — de variantentabel staat in shared/givenNameVariants.json en wordt
 * ook door de server gelezen (shared/archiveNameMatching.js). Client en server
 * hadden elk een eigen kopie die uit de pas liep. `voornaamCanoniek` hieronder
 * staat in beide bestanden in dezelfde vorm; tests/unit/voornaamCanoniek.pariteit
 * houdt ze gelijk op een gedeelde fixture-lijst.
 */
const GIVEN_NAME_VARIANTS_RAW = variantenData.varianten as string[][];

// Build bidirectional lookup: variant -> canonical form
const variantToCanonical = new Map<string, string>();
const canonicalToVariants = new Map<string, Set<string>>();

for (const [canonical, ...variants] of GIVEN_NAME_VARIANTS_RAW) {
  const allForms = [canonical, ...variants];
  const variantSet = new Set(allForms);
  canonicalToVariants.set(canonical, variantSet);
  for (const v of allForms) {
    variantToCanonical.set(v, canonical);
  }
}

/**
 * Orthografische varianten die in archieven door elkaar lopen: ck/c → k,
 * ph → f, ij → y, h na een medeklinker weg (th, gh, rh, kh), ae → a, dubbele
 * letters enkel. Spiegel van `orthografisch` in shared/archiveNameMatching.js.
 */
export function orthografisch(s: string): string {
  return s
    .replace(/ck/g, 'k')
    .replace(/c/g, 'k')
    .replace(/ph/g, 'f')
    .replace(/ij/g, 'y')
    .replace(/([^aeiouy])h/g, '$1')
    .replace(/ae/g, 'a')
    .replace(/(.)\1+/g, '$1');
}

/**
 * #1782 (NC #850) — orthografisch, plus de oude spelling van het verkleinwoord:
 * -gje ≈ -tje (Gerrigje/Gerritje, Neelgje/Neeltje). Alleen voor voornamen.
 */
function voornaamOrtho(s: string): string {
  // #1877 — z ≈ s in voornamen: Elizabeth/Elisabeth, Eliza/Elisa, Izaak/Isaak.
  return orthografisch(s.replace(/gje$/, 'tje').replace(/z/g, 's'));
}

const orthoNaarCanoniek = new Map<string, string>();
for (const [variant, canoniek] of variantToCanonical) {
  const o = voornaamOrtho(variant);
  if (!orthoNaarCanoniek.has(o)) orthoNaarCanoniek.set(o, canoniek);
}

/**
 * Latijnse naamvalsuitgangen, langste eerst. Het geslacht blijft behouden:
 * een mannelijke uitgang leidt nooit naar een vrouwelijke naam en omgekeerd —
 * Johannis wordt johannes, Joanna johanna, nooit elkaar.
 */
const LATIJNSE_UITGANGEN: [string, 'm' | 'v'][] = [
  ['ae', 'v'],
  ['am', 'v'],
  ['us', 'm'],
  ['es', 'm'],
  ['is', 'm'],
  ['um', 'm'],
  ['em', 'm'],
  ['a', 'v'],
  ['i', 'm'],
];

function zoekCanoniek(w: string): string | undefined {
  return variantToCanonical.get(w) ?? orthoNaarCanoniek.get(voornaamOrtho(w));
}

/**
 * NC #819 — de canonieke vorm van één voornaam. Volgorde: variantentabel,
 * dan orthografisch, dan Latijnse uitgang → stam (mannelijk) of stam + a
 * (vrouwelijk), opnieuw tegen de tabel. Joannis/Johannis → johannes,
 * Joanna/Joannae → johanna, Petri → petrus, Mariae → maria. Onbekend en
 * zonder Latijnse uitgang: de naam zelf, in kleine letters.
 */
export function voornaamCanoniek(naam: string): string {
  const w = stripDiacritics(
    String(naam || '')
      .toLowerCase()
      .trim()
  );
  if (!w) return '';
  const direct = zoekCanoniek(w);
  if (direct) return direct;
  for (const [uitgang, geslacht] of LATIJNSE_UITGANGEN) {
    if (!w.endsWith(uitgang) || w.length - uitgang.length < 3) continue;
    const stam = w.slice(0, -uitgang.length);
    const kandidaten =
      geslacht === 'm' ? [stam + 'us', stam + 'es', stam + 'is', stam] : [stam + 'a'];
    for (const k of kandidaten) {
      const hit = zoekCanoniek(k);
      if (hit && hit.endsWith('a') === (geslacht === 'v')) return hit;
    }
    return voornaamOrtho(geslacht === 'm' ? stam : stam + 'a');
  }
  // NC #839 — ook een naam buiten de tabel krijgt de spellingsregels:
  // "Mathijs" op de akte is "Matthijs" in de boom.
  return voornaamOrtho(w);
}

/**
 * Get the canonical form of a Dutch given name.
 */
export function getCanonicalGivenName(name: string): string {
  return voornaamCanoniek(name);
}

/**
 * Check if two given names are variants of the same canonical name.
 */
export function areGivenNameVariants(nameA: string, nameB: string): boolean {
  const canonA = voornaamCanoniek(nameA);
  return canonA !== '' && canonA === voornaamCanoniek(nameB);
}

/**
 * Get all known variants of a given name.
 */
export function getGivenNameVariants(name: string): string[] {
  const canonical = getCanonicalGivenName(name);
  const variants = canonicalToVariants.get(canonical);
  return variants ? Array.from(variants) : [name.toLowerCase().trim()];
}

// ---------------------------------------------------------------------------
// Tussenvoegsel normalization
// ---------------------------------------------------------------------------

// #1957 — de lijst staat in shared/voorouderGroepen.mjs: ook de server splitst er namen mee.
export { TUSSENVOEGSELS };

const PREFIX_EQUIVALENCES: Record<string, string> = {
  vd: 'van de',
  'v.d.': 'van de',
  'v.d': 'van de',
  'v/d': 'van de',
  vande: 'van de',
  vander: 'van der',
  vanden: 'van den',
};

export function normalizePrefix(raw: string | undefined | null): string {
  if (!raw) return '';
  let normalized = raw.toLowerCase().trim().replace(/\s+/g, ' ');
  normalized = normalized.replace(/^'+|'+$/g, '');
  return PREFIX_EQUIVALENCES[normalized] || normalized;
}

// ---------------------------------------------------------------------------
// Surname normalization
// ---------------------------------------------------------------------------

const SURNAME_EQUIVALENCES_RAW: string[][] = [
  ['smit', 'smith', 'smid', 'schmit', 'schmid'],
  ['jansen', 'janssen', 'janzen', 'jans', 'janssens'],
  ['bakker', 'backer', 'bakers'],
  ['de bruin', 'de bruijn', 'de bruyn', 'bruijn', 'bruin'],
  ['de vries', 'devries', 'vries'],
  ['visser', 'visscher', 'fischer'],
  ['mulder', 'molder'],
  ['de boer', 'deboer', 'boer'],
  ['de jong', 'dejong', 'jong'],
  ['meyer', 'meijer', 'meier', 'mayer', 'maier'],
  ['vos', 'voss', 'fos'],
  ['dekker', 'decker', 'dekkers'],
  ['dijkstra', 'dykstra'],
  ['van dijk', 'vandijk', 'van dyck', 'van dyk'],
  ['bos', 'bosch', 'bosse'],
  ['hendriks', 'hendrikse', 'hendricks', 'hendriksen'],
  ['peters', 'pieters', 'pieterse', 'peterse'],
  ['willems', 'willemse', 'willemsen'],
];

const surnameToCanonical = new Map<string, string>();
for (const [canonical, ...variants] of SURNAME_EQUIVALENCES_RAW) {
  surnameToCanonical.set(canonical, canonical);
  for (const v of variants) {
    surnameToCanonical.set(v, canonical);
  }
}

const OCR_SUBSTITUTIONS: [RegExp, string][] = [
  [/f/g, 's'],
  [/s/g, 'f'],
  [/u/g, 'n'],
  [/n/g, 'u'],
  [/c/g, 'e'],
  [/e/g, 'c'],
  [/rn/g, 'm'],
  [/ij/g, 'y'],
  [/y/g, 'ij'],
];

export function stripPrefix(fullSurname: string): [string, string] {
  const lower = fullSurname.toLowerCase().trim();
  for (const prefix of [...TUSSENVOEGSELS].sort((a, b) => b.length - a.length)) {
    if (lower.startsWith(prefix + ' ')) {
      return [prefix, lower.slice(prefix.length + 1).trim()];
    }
  }
  return ['', lower];
}

/**
 * Strip combining diacritical marks (NFD-decompose then remove). Used by
 * search-helpers so 'M\u00fcller' matcht 'Muller' en 'Ren\u00e9e' matcht 'Renee'.
 */
export function stripDiacritics(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function normalizeSurname(raw: string): string {
  if (!raw) return '';
  const [, root] = stripPrefix(raw);
  const cleaned = stripDiacritics(root.toLowerCase().trim());
  return surnameToCanonical.get(cleaned) || cleaned;
}

export function matchWithOcrCorrection(surnameA: string, surnameB: string): boolean {
  const normA = normalizeSurname(surnameA);
  const normB = normalizeSurname(surnameB);
  if (normA === normB) return true;

  for (const [pattern, replacement] of OCR_SUBSTITUTIONS) {
    if (normA.replace(pattern, replacement) === normB) return true;
    if (normB.replace(pattern, replacement) === normA) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Patronymic detection (pre-1812)
// ---------------------------------------------------------------------------

const PATRONYMIC_SUFFIXES = ['sz', 'sen', 'se', 's', 'zoon', 'zn', 'dr', 'dochter'];

export function isPatronymic(surname: string): boolean {
  const lower = surname.toLowerCase().trim();
  return PATRONYMIC_SUFFIXES.some(
    (suffix) => lower.endsWith(suffix) && lower.length > suffix.length + 2
  );
}

export function patronymicMatchesFather(patronymic: string, fatherGivenName: string): boolean {
  const pLower = patronymic.toLowerCase().trim();
  const fatherVariants = getGivenNameVariants(fatherGivenName);

  for (const variant of fatherVariants) {
    for (const suffix of PATRONYMIC_SUFFIXES) {
      if (pLower === variant + suffix) return true;
    }
    if (pLower.startsWith(variant) && pLower.length > variant.length) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Abbreviation detection
// ---------------------------------------------------------------------------

export function isAbbreviation(name: string): boolean {
  const trimmed = name.trim();
  return /^[A-Z]\.?$/.test(trimmed) || /^[A-Z][a-z]{0,3}\.$/.test(trimmed);
}

export function abbreviationMatches(abbrev: string, fullName: string): boolean {
  const a = abbrev.toLowerCase().replace(/\.$/, '').trim();
  const f = fullName.toLowerCase().trim();
  if (!a || !f) return false;
  return f.startsWith(a);
}
